'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { ConversationMgmtWelcomeModal } from './ConversationMgmtWelcomeModal';
import { ConversationMgmtCompleteModal } from './ConversationMgmtCompleteModal';
import { CONV_MGMT_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = CONV_MGMT_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * ConversationMgmtTourOverlay - Renders the Conversation Management tour step UI.
 *
 * Only renders when the active tour is 'conversation-management-tour'. Handles dynamic
 * navigation to the Voley Support conversation for open/close demos.
 */
export function ConversationMgmtTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isConvMgmtTour = activeTourId === 'conversation-management-tour';

  // Auto-advance polling
  useTourAutoAdvance(isConvMgmtTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isConvMgmtTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportConversation = searchResults?.items?.[0];
  const voleySupportId = voleySupportConversation?.id;

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle all step-specific navigation explicitly in the overlay
  useEffect(() => {
    if (!isConvMgmtTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Steps that need Voley Support conversation open
    const stepsNeedingConversation = [
      'explain_shared_number',
      'highlight_status_toggle',
    ];
    if (stepsNeedingConversation.includes(currentStep.id) && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info(`[ConvMgmtTour] Navigating to Voley Support for step: ${currentStep.id}`);
      return;
    }

    // Steps that need view-level navigation (no conversation)
    const viewNavigationMap: Record<string, string> = {
      highlight_all_tab: '/dashboard?view=all',
      highlight_closed_tab: '/dashboard?view=all',
      explain_reopen: '/dashboard?view=all',
      highlight_unread_btn: '/dashboard?view=all',
    };
    const targetUrl = viewNavigationMap[currentStep.id];
    if (targetUrl) {
      navigatedForStepRef.current = currentStep.id;
      router.push(targetUrl);
      reduxLogger.info(`[ConvMgmtTour] Navigating to ${targetUrl} for step: ${currentStep.id}`);
    }
  }, [isConvMgmtTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    if (voleySupportId) {
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info('[ConvMgmtTour] Navigating to Voley Support conversation');
    } else {
      router.push('/dashboard?view=all');
      reduxLogger.warn('[ConvMgmtTour] Voley Support conversation not found, navigating to dashboard');
    }
    nextStep();
  }, [voleySupportId, router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('conversation-management-tour');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isConvMgmtTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'conv_mgmt_welcome' && <ConversationMgmtWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'conv_mgmt_complete' && <ConversationMgmtCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
